import { TrialStatus } from '@/reducers/auth/userReducer'
import { PlanTier } from '@/actions/settings/billingActions'
import { isCompliancePath } from '@/features/compliance/lib/constants'
import {
  tierDisplayNamesFor,
  type PlanSegment,
} from '@/features/user/settings/sections/plan-change/lib/tierDisplayNames'

export const C_CORP_TAX_ENTITY = 'form_1120'

export const SIGNUP_PATHS = {
  referred: '/signup/r/:recipientCode',
  aboutYou: '/signup',
  aboutYourPractice: '/signup/about-your-practice',
  disqualified: '/signup/disqualified',
  waitlist: '/signup/waitlist',
  consult: '/signup/consult',
  // /signup/choose-plan points to payment and /signup/practice-journey points to choose plan within the routing
  payment: '/signup/choose-plan',
  choosePlan: '/signup/practice-journey',
  connectBank: '/signup/connect-bank',
  previousYearTax: '/signup/opt-in-tax',
  success: '/signup/success',
  emailCapture: '/signup/email',
  // Deliberately absent from every SIGNUP_STEPS* array and SIGNUP_STEP_MAP below: the
  // readiness flow is not part of the main step chain, so it must not drive the main
  // flow's progress indicator. The intake and report it leads to live in
  // `COMPLIANCE_PATHS` — this is only the account-creation step.
  //
  // Linked from the marketing site, so changing it needs a redirect.
  readinessAccount: '/signup/practice-readiness/account',
}

/**
 * Whether writing `incomingScreen` would overwrite a stored funnel position.
 *
 * A standard signup screen must never do so: the sweep that lands a funnel user on
 * `/signup/about-your-practice` mounts a screen that records its own position, which would
 * reclassify them as a standard-onboarding user permanently — nothing outside purchase
 * clears the field.
 */
export const wouldClobberFunnelPosition = (
  storedScreen: string | null | undefined,
  incomingScreen: string
): boolean =>
  typeof storedScreen === 'string' &&
  isCompliancePath(storedScreen) &&
  !isCompliancePath(incomingScreen)

// Screens from which standard signup cannot continue — `your-practice/helpers.ts` branches
// here instead of advancing, so a user parked on one is a legitimate funnel entrant rather
// than someone who wandered off the standard path. `consult` is a structural exit too, but
// its page writes no position, so it can never appear here.
const STANDARD_SIGNUP_EXITS: string[] = [
  SIGNUP_PATHS.disqualified,
  SIGNUP_PATHS.waitlist,
]

/**
 * Whether a user carrying `storedScreen` may be enrolled in the compliance funnel: they have
 * no position yet, are already in the funnel, or are parked on a standard-signup exit. A user
 * still mid-signup must not be converted.
 */
export const canEnterComplianceFunnel = (
  storedScreen: string | null | undefined
): boolean =>
  !storedScreen ||
  isCompliancePath(storedScreen) ||
  STANDARD_SIGNUP_EXITS.includes(storedScreen)

export const SIGNUP_STEPS = [
  'About You',
  'Your Practice',
  'Choose Plan',
  'Payment',
]
export const SIGNUP_STEPS_TAXES = [
  'About You',
  'Your Practice',
  'Choose Plan',
  'Your Tax Needs',
  'Payment',
]

export const SIGNUP_STEP_MAP = {
  [SIGNUP_PATHS.emailCapture]: 'Get Started',
  [SIGNUP_PATHS.aboutYou]: 'About You',
  [SIGNUP_PATHS.aboutYourPractice]: 'Your Practice',
  [SIGNUP_PATHS.choosePlan]: 'Choose Plan',
  [SIGNUP_PATHS.previousYearTax]: 'Your Tax Needs',
  [SIGNUP_PATHS.disqualified]: 'Your Tax Needs',
  [SIGNUP_PATHS.payment]: 'Payment',
  [SIGNUP_PATHS.success]: 'Payment',
}

type PracticeJourneySelection = {
  value: PlanTier
  imageAlt: string
  imageUrl: string
  title: string
  subtitle: string
}

export const PRACTICE_JOURNEY_SELECTIONS: Record<
  'lite' | 'essential' | 'premium',
  PracticeJourneySelection
> &
  Partial<Record<PlanTier, PracticeJourneySelection>> = {
  [PlanTier.lite]: {
    value: PlanTier.lite,
    imageAlt: 'green chair and table',
    imageUrl:
      'https://heard-images.s3.amazonaws.com/assets/green_chair_table.svg',
    title: 'Lite',
    subtitle:
      'Done-for-you bookkeeping, financial reports, and quarterly taxes for solo sole proprietors.',
  },
  [PlanTier.essential]: {
    value: PlanTier.essential,
    imageAlt: 'yellow chair and table',
    imageUrl:
      'https://heard-images.s3.amazonaws.com/assets/yellow_chair_table.svg',
    title: 'Essential',
    subtitle:
      'Done-for-you bookkeeping, financial reports, and year-round taxes for solo sole proprietors.',
  },
  [PlanTier.essentialPlus]: {
    value: PlanTier.essentialPlus,
    imageAlt: 'orange couch and floor lamp',
    imageUrl: 'https://heard-images.s3.amazonaws.com/assets/couch.svg',
    title: 'Premium',
    // Placeholder copy pending final marketing subtitle for this tier.
    subtitle:
      'Done-for-you bookkeeping, financial reports, and year-round taxes with a dedicated expert for solo sole proprietors.',
  },
  [PlanTier.premium]: {
    value: PlanTier.premium,
    imageAlt: 'two green chairs',
    imageUrl:
      'https://heard-images.s3.amazonaws.com/assets/two_green_chairs.svg',
    title: 'Essential',
    subtitle:
      'Done-for-you bookkeeping, financial reports, year-round taxes, and payroll for solo S corporations or group practices.',
  },
  [PlanTier.premiumPlus]: {
    value: PlanTier.premiumPlus,
    imageAlt: 'orange couch and floor lamp',
    imageUrl: 'https://heard-images.s3.amazonaws.com/assets/couch.svg',
    title: 'Premium',
    // Placeholder copy pending final marketing subtitle for this tier.
    subtitle:
      'Done-for-you bookkeeping, financial reports, year-round taxes, and payroll with a dedicated expert for solo S corporations or group practices.',
  },
}

export const practiceJourneyTitle = (
  key: 'essential' | 'premium',
  viewer: PlanSegment
): string => tierDisplayNamesFor(viewer)[key]

const FREE_TRIAL_DAYS = 30

/** Kept separate from `FREE_TRIAL_DAYS` despite both being 30: this one is a Growth test input and is expected to move. */
const FREE_TRIAL_2026_DAYS = 30

export const FREE_TRIAL_PROMO = 'freetrialpromo'
export const FREE_TRIAL_REFERRAL = 'referral'
export const FREE_TRIAL_2026 = 'freetrial2026'

/**
 * Eligibility and length share one source so they cannot drift apart: a code
 * grants a trial if and only if it is a key here. Retired codes
 * (`freetrialpromo`, `freetrial2025`) are absent.
 */
const TRIAL_CODE_LENGTH_DAYS: Readonly<Record<string, number>> = {
  [FREE_TRIAL_REFERRAL]: FREE_TRIAL_DAYS,
  [FREE_TRIAL_2026]: FREE_TRIAL_2026_DAYS,
}

/**
 * Own-property lookup, never `in` or a bare index: the code comes straight from
 * the URL, and both of those resolve inherited names like `toString` to a
 * function, which would pass as an eligible code and reach Stripe as a length.
 * Callers must handle `undefined` rather than defaulting — a default length is
 * what previously described 14-day trials as 30-day ones.
 */
export const fetchFreeTrialLength = (
  trialCode: string | null | undefined
): number | undefined =>
  trialCode &&
  Object.prototype.hasOwnProperty.call(TRIAL_CODE_LENGTH_DAYS, trialCode)
    ? TRIAL_CODE_LENGTH_DAYS[trialCode]
    : undefined

export const isEligibleTrialCode = (
  trialCode: string | null | undefined
): boolean => fetchFreeTrialLength(trialCode) !== undefined

export const isFreeTrialEnabled = (
  freeTrialSearchParamValue: string | null,
  userFreeTrialStatus: TrialStatus
): boolean =>
  isEligibleTrialCode(freeTrialSearchParamValue) &&
  userFreeTrialStatus === TrialStatus.unredeemed
